import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Dialog,
  DialogContent,
  LinearProgress,
  useTheme,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

// Styled components - Premium Modal Design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    padding: 0,
    minWidth: '520px',
    maxWidth: '720px',
    width: '90%',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      minWidth: '95%',
      margin: theme.spacing(1),
      borderRadius: theme.spacing(2),
    },
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(8px)',
  },
}));

// Modal Header
const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 3, 2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

// Progress Section - Clean & Modern
const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4, 3),
  backgroundColor: theme.palette.background.paper,
}));

const ProgressBarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  marginBottom: theme.spacing(3),
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: theme.palette.grey[100],
  '& .MuiLinearProgress-bar': {
    borderRadius: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
  },
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: theme.palette.primary.main,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  lineHeight: 1,
}));

const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  opacity: 0.8,
}));

// Status Indicator
const StatusIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(2),
  backgroundColor: `${theme.palette.primary.main}08`,
  border: `1px solid ${theme.palette.primary.main}20`,
  marginTop: theme.spacing(2),
}));

const StatusText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.primary.main,
  fontWeight: 500,
}));

// Carousel container - Premium 3-section queue
const SectionsCarousel = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '480px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: 0,
  padding: theme.spacing(4, 3),
  backgroundColor: theme.palette.grey[50],
  
  // Subtle gradient masks for smooth fade
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '120px',
    background: `linear-gradient(to bottom, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[50]}80 50%, ${theme.palette.grey[50]}00 100%)`,
    zIndex: 2,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '120px',
    background: `linear-gradient(to top, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[50]}80 50%, ${theme.palette.grey[50]}00 100%)`,
    zIndex: 2,
    pointerEvents: 'none',
  },
}));

const SectionsScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
}));

// Premium Section Cards
const SectionCard = styled(Card)<{ 
  isActive?: boolean; 
  isCompleted?: boolean;
  queuePosition?: 'top' | 'center' | 'bottom';
}>(
  ({ theme, isActive, isCompleted, queuePosition = 'center' }) => ({
    marginBottom: theme.spacing(3),
    minHeight: '140px',
    width: '100%',
    maxWidth: '500px',
    borderRadius: theme.spacing(2),
    border: isActive
      ? `2px solid ${theme.palette.primary.main}`
      : isCompleted
      ? `1px solid ${theme.palette.success.main}`
      : `1px solid ${theme.palette.divider}`,
    backgroundColor: isActive
      ? theme.palette.background.paper
      : isCompleted
      ? theme.palette.background.paper
      : theme.palette.background.paper,
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    
    // 3-section queue positioning
    transform: queuePosition === 'center' 
      ? 'scale(1.05) translateY(0) translateZ(0)' 
      : queuePosition === 'top'
      ? 'scale(0.88) translateY(-80px) translateZ(0)'
      : 'scale(0.88) translateY(80px) translateZ(0)',
    
    opacity: queuePosition === 'center' 
      ? 1 
      : queuePosition === 'top'
      ? 0.5
      : 0.4,
    
    filter: queuePosition === 'center' 
      ? 'blur(0px) brightness(1.02)' 
      : queuePosition === 'top'
      ? 'blur(1px) brightness(0.95)'
      : 'blur(1.5px) brightness(0.9)',
    
    boxShadow: isActive && queuePosition === 'center'
      ? `0 12px 32px ${theme.palette.primary.main}25`
      : isCompleted && queuePosition === 'top'
      ? `0 4px 16px ${theme.palette.success.main}15`
      : queuePosition === 'bottom'
      ? `0 2px 8px rgba(0,0,0,0.06)`
      : '0 1px 4px rgba(0,0,0,0.04)',
    
    pointerEvents: queuePosition === 'center' ? 'auto' : 'none',
    willChange: 'transform, opacity, filter',
    zIndex: queuePosition === 'center' ? 3 : queuePosition === 'top' ? 2 : 1,
    
    // Subtle pulse animation for center active section
    ...(isActive && queuePosition === 'center' && {
      animation: 'centerPulse 3s ease-in-out infinite',
      '@keyframes centerPulse': {
        '0%, 100%': {
          boxShadow: `0 12px 32px ${theme.palette.primary.main}25`,
          borderColor: theme.palette.primary.main,
        },
        '50%': {
          boxShadow: `0 16px 40px ${theme.palette.primary.main}35`,
          borderColor: theme.palette.primary.light,
        },
      },
    }),
  })
);

// Section Header - Clean & Professional
const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const SectionTitle = styled(Typography)<{ isCurrent?: boolean }>(({ theme, isCurrent }) => ({
  fontSize: isCurrent ? '1.1rem' : '1rem',
  fontWeight: isCurrent ? 600 : 500,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  transition: 'all 0.3s ease',
}));

const SectionContent = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  marginTop: theme.spacing(1),
  padding: theme.spacing(1, 0),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// Status Badge
const StatusBadge = styled(Box)<{ 
  status: 'generating' | 'completed' | 'pending';
  isCurrent?: boolean;
}>(({ theme, status, isCurrent }) => ({
  width: isCurrent ? 36 : 32,
  height: isCurrent ? 36 : 32,
  borderRadius: '50%',
  backgroundColor: status === 'generating'
    ? theme.palette.primary.main
    : status === 'completed'
    ? theme.palette.success.main
    : theme.palette.grey[300],
  color: status === 'pending' ? theme.palette.grey[600] : 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: isCurrent ? '1rem' : '0.875rem',
  transition: 'all 0.3s ease',
  boxShadow: status === 'generating' && isCurrent
    ? `0 4px 12px ${theme.palette.primary.main}40`
    : status === 'completed'
    ? `0 2px 6px ${theme.palette.success.main}30`
    : 'none',
}));

// Loading Spinner
const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  },
}));

// Add blink animation for cursor
const blinkKeyframes = `
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

// Props interface
interface GenerationProgressPopupMAIQProps {
  open?: boolean;
  streamingData?: {
    message?: {
      conversationId?: string;
      isFinal?: boolean;
      sections?: Array<{
        index: number;
        main?: string;
        sub_text?: string;
      }>;
      text?: {
        key: {
          index: number;
          main?: string;
          sub_text?: string;
        };
        value: string;
      };
    };
  };
  streamingText?: string;
  sections?: Array<{
    index: number;
    main?: string;
    sub_text?: string;
  }>;
  currentSection?: {
    index: number;
    main?: string;
    sub_text?: string;
  };
  isGenerating?: boolean;
  isFinal?: boolean;
  showProgress?: boolean;
  message?: string;
  onClose?: () => void;
}

const GenerationProgressPopupMAIQ: React.FC<GenerationProgressPopupMAIQProps> = ({
  open = true,
  streamingData,
  streamingText = '',
  sections = [],
  currentSection,
  isGenerating = true,
  isFinal = false,
  showProgress = true,
  message = 'Crafting your message...',
  onClose,
}) => {
  const theme = useTheme();
  const [displayText, setDisplayText] = useState('');
  const [textArray, setTextArray] = useState<string[]>([]);
  const [processedSections, setProcessedSections] = useState<
    Array<{
      index: number;
      main?: string;
      sub_text?: string;
    }>
  >([]);
  const [filledSections, setFilledSections] = useState<Set<number>>(new Set());
  const [displayProgress, setDisplayProgress] = useState(0);
  const [sectionTexts, setSectionTexts] = useState<Map<number, string>>(new Map());

  // Calculate actual progress percentage
  const calculateActualProgress = () => {
    const sectionsToUse = processedSections.length > 0 ? processedSections : sections;
    const totalSections = Math.max(sectionsToUse.length, 1);
    const completedSections = filledSections.size;
    const calculatedProgress = Math.round((completedSections / totalSections) * 100);

    // Ensure progress never exceeds 100% and never goes below 0%
    return Math.min(Math.max(calculatedProgress, 0), 100);
  };

  // Update display progress when actual progress changes
  useEffect(() => {
    const actualProgress = calculateActualProgress();

    if (actualProgress > displayProgress) {
      // Start animation from current display progress to actual progress
      const interval = setInterval(() => {
        setDisplayProgress(prev => {
          const nextValue = prev + Math.round(Math.random() * 3) + 1; // Random increment between 1-4
          // Stop at actual progress, don't exceed it
          return Math.min(nextValue, actualProgress);
        });
      }, Math.random() * 100 + 50); // Random between 50-150ms

      return () => clearInterval(interval);
    } else if (actualProgress === 0) {
      setDisplayProgress(0);
    }
  }, [filledSections, processedSections, sections, displayProgress]);

  // Process streaming data
  useEffect(() => {
    if (streamingData?.message) {
      const { sections: dataSections, text, isFinal: dataIsFinal } = streamingData.message;

      // Update sections if available
      if (dataSections && dataSections.length > 0) {
        setProcessedSections(dataSections);
      }

      // Process text data
      if (text && text.value) {
        const sectionIndex = text.key.index;
        const textValue = text.value;
        
        // Filter out single-word type field values (like "Disclaimer", "Greeting", etc)
        // Only store actual content (multiple words or sentences)
        const isActualContent = textValue.trim().split(/\s+/).length > 1;
        
        // Update section texts only if it's actual content
        if (isActualContent) {
          setSectionTexts(prev => {
            const newMap = new Map(prev);
            newMap.set(sectionIndex, textValue);
            return newMap;
          });
        }

        setTextArray(prev => {
          const newArray = [...prev, textValue];
          // Fill sections based on text array length and actual sections count
          const sectionsToUse = processedSections.length > 0 ? processedSections : sections;
          const totalSections = Math.max(sectionsToUse.length, 1);
          const newFilledSections = new Set<number>();
          for (let i = 0; i < Math.min(newArray.length, totalSections); i++) {
            newFilledSections.add(i + 1);
          }
          setFilledSections(newFilledSections);
          return newArray;
        });
        
        // Only update display text if it's actual content
        if (isActualContent) {
          setDisplayText(textValue);
        }
      }
    }
  }, [streamingData]);

  // Typewriter effect for streaming text
  useEffect(() => {
    if (streamingText && isGenerating) {
      setDisplayText(streamingText);
    }
  }, [streamingText, isGenerating]);

  const sectionsToUse = processedSections.length > 0 ? processedSections : sections;

  // Find the last filled section (currently generating/processing)
  const getGeneratingSectionId = () => {
    const filledSectionsArray = Array.from(filledSections).sort((a, b) => a - b);

    // If no sections are filled, no section is generating
    if (filledSectionsArray.length === 0) {
      return null;
    }

    // Return the last filled section (most recently completed)
    return filledSectionsArray[filledSectionsArray.length - 1];
  };

  const generatingSectionId = getGeneratingSectionId();

  // Get status for each section
  const getSectionStatus = (sectionId: number) => {
    if (generatingSectionId === sectionId && isGenerating) {
      return 'generating';
    }
    if (filledSections.has(sectionId)) {
      return 'completed';
    }
    return 'pending';
  };

  // Get the 3-section queue to display (previous, current, next)
  const getVisibleSectionsQueue = (): Array<{
    section: { index: number; main?: string; sub_text?: string };
    position: 'above' | 'current' | 'below';
    queuePosition: 'top' | 'center' | 'bottom';
  }> => {
    if (sectionsToUse.length === 0) return [];

    // Find current generating section index
    const currentGeneratingIndex = sectionsToUse.findIndex(
      s => getSectionStatus(s.index) === 'generating'
    );

    // If no section is generating, find last completed
    let centerIndex = currentGeneratingIndex;
    if (centerIndex === -1) {
      const completedSections = sectionsToUse.filter(s => 
        getSectionStatus(s.index) === 'completed'
      );
      if (completedSections.length > 0) {
        const lastCompleted = completedSections[completedSections.length - 1];
        centerIndex = sectionsToUse.findIndex(s => s.index === lastCompleted.index);
      } else {
        centerIndex = 0; // Default to first section
      }
    }

    // Get the 3-section queue: [previous, current, next]
    const queue: Array<{
      section: { index: number; main?: string; sub_text?: string };
      position: 'above' | 'current' | 'below';
      queuePosition: 'top' | 'center' | 'bottom';
    }> = [];
    
    // Previous section (above)
    if (centerIndex > 0) {
      queue.push({
        section: sectionsToUse[centerIndex - 1],
        position: 'above',
        queuePosition: 'top'
      });
    }

    // Current section (center)
    queue.push({
      section: sectionsToUse[centerIndex],
      position: 'current',
      queuePosition: 'center'
    });

    // Next section (below)
    if (centerIndex < sectionsToUse.length - 1) {
      queue.push({
        section: sectionsToUse[centerIndex + 1],
        position: 'below',
        queuePosition: 'bottom'
      });
    }

    return queue;
  };

  const visibleQueue = getVisibleSectionsQueue();

  return (
    <>
      <style>{blinkKeyframes}</style>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Modal Header */}
          <ModalHeader>
            <HeaderTitle>
              <PlayCircleOutlineIcon sx={{ fontSize: '1.5rem', color: theme.palette.primary.main }} />
              Hang tight… crafting your message.
            </HeaderTitle>
            {onClose && (
              <CloseButton onClick={onClose} size="small">
                <CloseIcon fontSize="small" />
              </CloseButton>
            )}
          </ModalHeader>

          {/* Progress Section */}
          <ProgressContainer>
            <ProgressText variant="h3">{displayProgress}%</ProgressText>
            <ProgressLabel>Progress Completed</ProgressLabel>

            <ProgressBarContainer>
              <ProgressBar
                variant="determinate"
                value={displayProgress}
              />
            </ProgressBarContainer>

            {/* Status Indicator */}
            {currentSection && isGenerating && (
              <StatusIndicator>
                <LoadingSpinner size={16} />
                <StatusText>
                  Currently working on: <strong>{currentSection.main || 'Section ' + currentSection.index}</strong>
                </StatusText>
              </StatusIndicator>
            )}
          </ProgressContainer>

          {/* Main content */}
          {!displayText && isGenerating ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6,
              px: 3,
              backgroundColor: theme.palette.grey[50],
            }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Getting things ready...
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Sections carousel - 3-section queue */}
              <SectionsCarousel>
                <SectionsScrollContainer>
                  {visibleQueue.map((item) => {
                    const { section, queuePosition } = item;
                    const status = getSectionStatus(section.index);
                    const isActive = status === 'generating';
                    const isCompleted = status === 'completed';
                    const isPending = status === 'pending';
                    const sectionText = sectionTexts.get(section.index);

                    return (
                      <SectionCard 
                        key={section.index}
                        isActive={isActive} 
                        isCompleted={isCompleted}
                        queuePosition={queuePosition}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <SectionHeader>
                            <SectionTitle isCurrent={queuePosition === 'center'}>
                              <StatusBadge 
                                status={status} 
                                isCurrent={queuePosition === 'center'}
                              >
                                {section.index.toString().padStart(2, '0')}
                              </StatusBadge>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                {section.main && (
                                  <Typography variant="body1" sx={{ fontWeight: 'inherit', fontSize: 'inherit' }}>
                                    {section.main}
                                  </Typography>
                                )}
                                {section.sub_text && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
                                    {section.sub_text}
                                  </Typography>
                                )}
                              </Box>
                            </SectionTitle>

                            {/* Status indicator */}
                            {isActive && queuePosition === 'center' && (
                              <LoadingSpinner size={20} thickness={4} />
                            )}
                            {isCompleted && (
                              <CheckCircleIcon
                                sx={{ 
                                  color: theme.palette.success.main, 
                                  fontSize: 24,
                                  filter: queuePosition === 'center' ? 'none' : 'opacity(0.7)'
                                }}
                              />
                            )}
                            {isPending && queuePosition === 'bottom' && (
                              <Chip
                                label="Pending"
                                size="small"
                                sx={{
                                  backgroundColor: theme.palette.grey[100],
                                  color: theme.palette.text.secondary,
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              />
                            )}
                          </SectionHeader>
                        </CardContent>
                      </SectionCard>
                    );
                  })}
                </SectionsScrollContainer>
              </SectionsCarousel>
            </Box>
          )}

          {/* Final completion message */}
          {isFinal && (
            <Fade in={isFinal} timeout={800}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 4,
                  backgroundColor: `${theme.palette.success.main}08`,
                  borderTop: `1px solid ${theme.palette.success.main}20`,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.success.main,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: '1.25rem' }} /> 
                  All done! Your message is ready.
                </Typography>
              </Box>
            </Fade>
          )}
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default GenerationProgressPopupMAIQ;

